import {
  useEffect,
  useRef,
  useState,
  type ImgHTMLAttributes,
} from "react";

import { shopifyCdnImageUrl } from "../lib/shopifyCdnImageUrl";

const SHOPIFY_MAX_DIM = 5760;
const BUCKET_STEP = 32;

function clampDim(n: number): number {
  if (!Number.isFinite(n)) return 1;
  return Math.min(SHOPIFY_MAX_DIM, Math.max(1, n));
}

function bucketDim(n: number): number {
  return clampDim(Math.ceil(n / BUCKET_STEP) * BUCKET_STEP);
}

function readObserverDimensions(entry: ResizeObserverEntry): {
  widthPx: number;
  heightPx: number;
} | null {
  const dpr = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;

  const dpcb = entry.devicePixelContentBoxSize?.[0];
  if (dpcb && dpcb.inlineSize > 0 && dpcb.blockSize > 0) {
    return {
      widthPx: dpcb.inlineSize,
      heightPx: dpcb.blockSize,
    };
  }

  const cr = entry.contentRect;
  if (cr.width > 0 && cr.height > 0) {
    return {
      widthPx: cr.width * dpr,
      heightPx: cr.height * dpr,
    };
  }

  return null;
}

export type ShopifyObservedImageProps = Omit<
  ImgHTMLAttributes<HTMLImageElement>,
  "src"
> & {
  src: string;
  fallbackWidth: number;
  fallbackHeight: number;
  shopify?: {
    crop?: "top" | "center" | "bottom" | "left" | "right" | "region";
  };
};

export function ShopifyObservedImage({
  src,
  alt,
  className,
  fallbackWidth,
  fallbackHeight,
  shopify,
  ...imgProps
}: ShopifyObservedImageProps) {
  const ref = useRef<HTMLImageElement>(null);
  const [dims, setDims] = useState({
    w: fallbackWidth,
    h: fallbackHeight,
  });

  useEffect(() => {
    setDims({ w: fallbackWidth, h: fallbackHeight });
  }, [src, fallbackWidth, fallbackHeight]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let raf = 0;

    const mergeDims = (w: number, h: number) => {
      setDims((prev) => (prev.w === w && prev.h === h ? prev : { w, h }));
    };

    const flush = (entry: ResizeObserverEntry) => {
      const sizes = readObserverDimensions(entry);
      if (!sizes) return;
      mergeDims(bucketDim(sizes.widthPx), bucketDim(sizes.heightPx));
    };

    const ro = new ResizeObserver((entries) => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const entry = entries[entries.length - 1];
        if (entry) flush(entry);
      });
    });

    try {
      ro.observe(el, { box: "device-pixel-content-box" });
    } catch {
      ro.observe(el);
    }

    raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const dpr = window.devicePixelRatio || 1;
        const r = el.getBoundingClientRect();
        if (r.width >= 1 && r.height >= 1) {
          mergeDims(bucketDim(r.width * dpr), bucketDim(r.height * dpr));
        }
      });
    });

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [src]);

  const optimizedSrc = shopifyCdnImageUrl(src, {
    width: dims.w,
    height: dims.h,
    crop: shopify?.crop,
  });

  return (
    <img
      ref={ref}
      src={optimizedSrc}
      alt={alt}
      className={className}
      {...imgProps}
    />
  );
}
